import type { HeartRateEvent } from '@model/DeviceConfiguration';
import { NUMERIC_TOLERANCE, REPAIR_DECIMAL_PLACES } from './constants';
import {
  countExpectedGapInsertions,
  normalizeTimestampToSecond,
} from './detectHrDataIssues';
import type { AiRepairOperations, HrDataIssues } from './types';

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const round = (value: number, digits = REPAIR_DECIMAL_PLACES) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const average = (values: number[]) => {
  if (values.length === 0) return undefined;
  return round(values.reduce((sum, value) => sum + value, 0) / values.length);
};

const interpolate = (
  start: number | null | undefined,
  end: number | null | undefined,
  step: number,
  totalSteps: number
) => {
  if (isFiniteNumber(start) && isFiniteNumber(end)) {
    return round(start + ((end - start) * step) / totalSteps);
  }
  if (isFiniteNumber(start)) return round(start);
  if (isFiniteNumber(end)) return round(end);
  return undefined;
};

const groupEventsBySecond = (events: HeartRateEvent[]) => {
  const grouped = new Map<number, HeartRateEvent[]>();
  events.forEach(event => {
    if (!isFiniteNumber(event.timestamp)) return;
    const second = normalizeTimestampToSecond(event.timestamp);
    const current = grouped.get(second) ?? [];
    current.push(event);
    grouped.set(second, current);
  });
  return grouped;
};

const buildMergedSecondMap = (
  grouped: Map<number, HeartRateEvent[]>,
  issues: HrDataIssues
) => {
  const duplicateSeconds = new Set(
    issues.duplicates.map(item => item.secondTimestamp)
  );
  const merged = new Map<number, HeartRateEvent>();

  grouped.forEach((groupEvents, secondTimestamp) => {
    if (duplicateSeconds.has(secondTimestamp)) {
      const first = groupEvents[0];
      merged.set(secondTimestamp, {
        timestamp: secondTimestamp,
        deviceCode: first.deviceCode,
        hr:
          average(groupEvents.map(item => item.hr).filter(isFiniteNumber)) ?? 0,
        hrv: average(groupEvents.map(item => item.hrv).filter(isFiniteNumber)),
        lf: average(groupEvents.map(item => item.lf).filter(isFiniteNumber)),
        hf: average(groupEvents.map(item => item.hf).filter(isFiniteNumber)),
      });
      return;
    }

    if (groupEvents.length === 1) {
      merged.set(secondTimestamp, {
        ...groupEvents[0],
        timestamp: secondTimestamp,
      });
    }
  });

  return merged;
};

export const buildDeterministicRepairOperations = (
  originalEvents: HeartRateEvent[],
  issues: HrDataIssues
): AiRepairOperations => {
  const grouped = groupEventsBySecond(originalEvents);

  const duplicateMerges = issues.duplicates.map(duplicate => {
    const groupEvents = grouped.get(duplicate.secondTimestamp) ?? [];
    return {
      secondTimestamp: duplicate.secondTimestamp,
      merged: {
        hr:
          average(groupEvents.map(item => item.hr).filter(isFiniteNumber)) ?? 0,
        hrv: average(groupEvents.map(item => item.hrv).filter(isFiniteNumber)),
        lf: average(groupEvents.map(item => item.lf).filter(isFiniteNumber)),
        hf: average(groupEvents.map(item => item.hf).filter(isFiniteNumber)),
        deviceCode: groupEvents[0]?.deviceCode,
      },
      sourceTimestamps: groupEvents.map(item => item.timestamp),
    };
  });

  const mergedSecondMap = buildMergedSecondMap(grouped, issues);
  const gapInsertions = issues.gaps.flatMap(gap => {
    const fromEvent = mergedSecondMap.get(gap.fromTimestamp);
    const toEvent = mergedSecondMap.get(gap.toTimestamp);
    const deltaSec = Math.round((gap.toTimestamp - gap.fromTimestamp) / 1000);

    return gap.missingTimestamps.map(timestamp => {
      const step = (timestamp - gap.fromTimestamp) / 1000;
      return {
        timestamp,
        hr: interpolate(fromEvent?.hr, toEvent?.hr, step, deltaSec) ?? 0,
        hrv: interpolate(fromEvent?.hrv, toEvent?.hrv, step, deltaSec),
        lf: interpolate(fromEvent?.lf, toEvent?.lf, step, deltaSec),
        hf: interpolate(fromEvent?.hf, toEvent?.hf, step, deltaSec),
        fromTimestamp: gap.fromTimestamp,
        toTimestamp: gap.toTimestamp,
      };
    });
  });

  return { duplicateMerges, gapInsertions };
};

export const normalizeRepairOperations = (
  originalEvents: HeartRateEvent[],
  issues: HrDataIssues,
  aiOperations: AiRepairOperations
): AiRepairOperations => {
  const deterministic = buildDeterministicRepairOperations(
    originalEvents,
    issues
  );

  const aiDuplicateSeconds = new Set(
    aiOperations.duplicateMerges.map(item => item.secondTimestamp)
  );
  const aiGapTimestamps = new Set(
    aiOperations.gapInsertions.map(item => item.timestamp)
  );

  const duplicateMerges = deterministic.duplicateMerges.map(item => {
    if (!aiDuplicateSeconds.has(item.secondTimestamp)) {
      return item;
    }
    const aiMatch = aiOperations.duplicateMerges.find(
      candidate => candidate.secondTimestamp === item.secondTimestamp
    );
    return {
      ...item,
      sourceTimestamps:
        aiMatch?.sourceTimestamps.length === item.sourceTimestamps.length
          ? aiMatch.sourceTimestamps
          : item.sourceTimestamps,
    };
  });

  issues.duplicates.forEach(duplicate => {
    if (
      !duplicateMerges.some(
        item => item.secondTimestamp === duplicate.secondTimestamp
      )
    ) {
      duplicateMerges.push(
        deterministic.duplicateMerges.find(
          item => item.secondTimestamp === duplicate.secondTimestamp
        )!
      );
    }
  });

  const gapInsertions = deterministic.gapInsertions.map(item => {
    if (!aiGapTimestamps.has(item.timestamp)) {
      return item;
    }
    return item;
  });

  return {
    duplicateMerges: duplicateMerges.sort(
      (left, right) => left.secondTimestamp - right.secondTimestamp
    ),
    gapInsertions: gapInsertions.sort(
      (left, right) => left.timestamp - right.timestamp
    ),
  };
};

export const buildRepairSummary = (
  originalEvents: HeartRateEvent[],
  issues: HrDataIssues,
  operations: AiRepairOperations,
  applied: {
    repairedEvents: HeartRateEvent[];
    repairedOnly: HeartRateEvent[];
    duplicateMerges: number;
    gapFillCount: number;
  }
) => {
  const grouped = groupEventsBySecond(originalEvents);
  const uniqueOriginalSeconds = grouped.size;
  const expectedGapInsertions = countExpectedGapInsertions(issues);
  const expectedRepairedCount = uniqueOriginalSeconds + expectedGapInsertions;

  return {
    rawOriginalEventCount: originalEvents.length,
    uniqueOriginalSeconds,
    expectedRepairedCount,
    actualRepairedCount: applied.repairedEvents.length,
    duplicateMergeOperations: applied.duplicateMerges,
    gapInsertionOperations: applied.gapFillCount,
    correctionsCount: applied.repairedOnly.length,
    countFormula:
      'actualRepairedCount = uniqueOriginalSeconds + gapInsertionOperations',
    correctionsFormula:
      'correctionsCount = duplicateMergeOperations + gapInsertionOperations',
    countsMatchExpected:
      Math.abs(applied.repairedEvents.length - expectedRepairedCount) <=
      NUMERIC_TOLERANCE,
    duplicateEvidence: operations.duplicateMerges.map(item => ({
      secondTimestamp: item.secondTimestamp,
      sourceTimestamps: item.sourceTimestamps,
      merged: item.merged,
      method: 'arithmetic_mean_of_sources',
    })),
    gapEvidenceSample: operations.gapInsertions.slice(0, 5).map(item => ({
      timestamp: item.timestamp,
      fromTimestamp: item.fromTimestamp,
      toTimestamp: item.toTimestamp,
      values: {
        hr: item.hr,
        hrv: item.hrv,
        lf: item.lf,
        hf: item.hf,
      },
      method: 'linear_interpolation_between_gap_boundaries',
    })),
  };
};
